import React, { useState } from 'react';
import { Platform, View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Header, ScreenLayout, FormHeader, Modal, colors, spacing, typography } from '../components';
import { VisitsList } from '../components/VisitsList';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useThemedStyles } from '../hooks/useThemedStyles';

export const VisitsListScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab } = useAppContext();
  const { t } = useLocale();
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={false}
      formHeader={
        <FormHeader
          title={t('visits.list.title')}
          actions={[
            {
              icon: 'add-circle-outline',
              onPress: () => setShowCreateMenu(true),
              label: t('visits.list.createVisit'),
              primary: true,
            },
            {
              icon: 'briefcase-outline',
              onPress: () => navigateTo('visits-campaigns'),
              label: t('visits.list.campaignsButton'),
            },
            {
              icon: 'navigate-outline',
              onPress: () => navigateTo('visit-route'),
              label: t('visits.list.routeButton'),
            },
            {
              icon: 'settings-outline',
              onPress: () => navigateTo('visits-setup'),
              label: t('visits.list.settingsButton'),
              accessibilityLabel: t('visits.list.settingsButton'),
            },
          ]}
        />
      }
    >
      <VisitsList />

      <Modal
        visible={showCreateMenu}
        onClose={() => setShowCreateMenu(false)}
        title={t('visits.list.createMenuTitle')}
        size="small"
      >
        <View style={styles.menuOptions}>
          <TouchableOpacity
            style={styles.menuOption}
            onPress={() => { setShowCreateMenu(false); navigateTo('visit-create'); }}
          >
            <Ionicons name="walk-outline" size={22} color={colors.primary as string} />
            <View style={styles.menuOptionTextWrap}>
              <Text style={styles.menuOptionTitle}>{t('visits.list.createVisit')}</Text>
              <Text style={styles.menuOptionSubtitle}>{t('visits.list.createVisitSubtitle')}</Text>
            </View>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.menuOption}
            onPress={() => { setShowCreateMenu(false); navigateTo('visits-campaigns'); }}
          >
            <Ionicons name="briefcase-outline" size={22} color={colors.primary as string} />
            <View style={styles.menuOptionTextWrap}>
              <Text style={styles.menuOptionTitle}>{t('visits.list.createCampaign')}</Text>
              <Text style={styles.menuOptionSubtitle}>{t('visits.list.createCampaignSubtitle')}</Text>
            </View>
          </TouchableOpacity>
        </View>
      </Modal>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  menuOptions: { gap: spacing.sm },
  menuOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border as string,
  },
  menuOptionTextWrap: { flex: 1 },
  menuOptionTitle: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  menuOptionSubtitle: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },
});
